import { useEffect } from 'react';
/**
 * Tracks input modality (mouse vs keyboard) and applies a data attribute to document.body.
 *
 * This is useful for applying different focus styles based on how the user is
 * interacting with the page. For example, you might want to show focus rings
 * only when the user is navigating with the keyboard.
 *
 * The body will have `data-modality="mouse"` or `data-modality="keyboard"` applied.
 *
 * Why this exists:
 * - `:focus-visible` doesn't work reliably when focus is moved programmatically
 *   (e.g., when a popover opens and focuses its first element)
 * - By tracking modality ourselves, we can apply focus styles based on the actual
 *   input method, not the browser's heuristics
 */
export function useInputModalityTracker() {
    useEffect(() => {
        const setModality = (modality) => {
            document.body.dataset.modality = modality;
        };
        const setMouseModality = () => setModality('mouse');
        const setKeyboardModality = (e) => {
            // Only switch to keyboard mode for navigation/activation keys
            // This prevents switching when typing in an input field
            if (e.key === 'Tab' || e.key?.startsWith('Arrow')) {
                setModality('keyboard');
            }
        };
        document.addEventListener('pointerdown', setMouseModality, true);
        document.addEventListener('keydown', setKeyboardModality, true);
        setMouseModality();
        return () => {
            document.removeEventListener('pointerdown', setMouseModality, true);
            document.removeEventListener('keydown', setKeyboardModality, true);
            // document.body may be null on unload/navigation.
            if (document.body) {
                delete document.body.dataset.modality;
            }
        };
    }, []);
}
//# sourceMappingURL=useInputModalityTracker.js.map